import { useState } from 'react';
import { FaSyncAlt, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { downloadReceivablesFromConvertXml, NoClosedReceivablesError } from '../../hooks/data/useReceivablesDownloader';

// Status da operação/contrato que impedem a reimportação de recebíveis.
const LOCKED_STATUSES = new Set(['Finalizado com sucesso', 'Cancelado', 'Aditado']);

interface Props {
    sequencialNumber: number;
    amendmentNumber?: number;
    // Recebe o payload já parseado do convert-xml (mesmo formato de handleJsonDataLoaded).
    onReimported: (data: Awaited<ReturnType<typeof downloadReceivablesFromConvertXml>>) => void;
    contractStatus?: string;
    disabled?: boolean;
}

const ReimportReceivablesButton: React.FC<Props> = ({ sequencialNumber, amendmentNumber, onReimported, contractStatus, disabled }) => {
    const [isLoading, setIsLoading] = useState(false);

    const isStatusLocked = !!contractStatus && LOCKED_STATUSES.has(contractStatus);
    const isDisabled = disabled || isLoading || isStatusLocked;

    const handleReimport = async () => {
        if (sequencialNumber <= 0) {
            toast.error('Contrato sem número sequencial para importar');
            return;
        }
        setIsLoading(true);
        try {
            const parsedData = await downloadReceivablesFromConvertXml(sequencialNumber, amendmentNumber);
            onReimported(parsedData);
            toast.success('Recebíveis importados com sucesso');
        } catch (error) {
            if (error instanceof NoClosedReceivablesError) {
                toast.info(error.message);
            } else {
                toast.error('Erro ao importar recebíveis');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <button type="button"
            onClick={handleReimport}
            disabled={isDisabled}
            title="Importar recebíveis do convert-xml"
            className="inline-flex items-center gap-2 bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-400 text-white font-medium rounded text-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
            {isLoading ? <FaSpinner className="animate-spin" /> : <FaSyncAlt />}
            Importar recebíveis
        </button>
    );
};

export default ReimportReceivablesButton;
